import { AnyAction } from 'redux';
import { call, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { CallEffect, ForkEffect, SelectEffect } from '@redux-saga/core/effects';
import {
  createHashedTaasData,
  getSelectedProduktlinie,
  StreckenTarifierer,
} from '@ps20-frontend/preis-lib';
import { App } from '@ps20-frontend/frontend-models';
import {
  StreckenTarifierungRequestEvents,
  TARIF_CHECKED,
  ZUSATZOPTION_CHECKED,
  ZUSATZOPTION_UNCHECKED,
} from '@ps20-frontend/shop-integration-redux';
import { TaasVhvV2Data } from '@ps20/bff-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { VhvActions } from '../vhvActions';
import { IAppState } from '../appState';
import {
  isEntschaedigungsgrenzeResetFromBff,
  isEntschaedigungsgrenzeValid,
} from '../model/entschaedigungsgrenzeRad.model';
import { buildTaasVHVDataForStrecke } from './transformer/vhvData.transformer';

export let streckenTarifierer: StreckenTarifierer<IAppState, TaasVhvV2Data>;

export function* streckenTarifierungVHV(): Generator<ForkEffect> {
  const requestActions = [
    TARIF_CHECKED,
    ZUSATZOPTION_CHECKED,
    ZUSATZOPTION_UNCHECKED,
    VhvActions.PREISE_REQUESTED,
    VhvActions.VERSSUMME_FAHRRAD_CHANGED,
    VhvActions.ENTSCHAEDIGUNGSGRENZE_RAD_RECEIVED,
    VhvActions.ENTSCHAEDIGUNGSGRENZE_RAD_CHANGED,
    VhvActions.EXTREMWETTERSCHUTZ_SELBSTBETEILIGUNG_CHANGED,
    VhvActions.SELBSTBETEILIGUNG_CHANGED,
    VhvActions.WERTSACHENSUMMEN_CHANGED,
    VhvActions.WERTSACHENSUMME_CHANGED,
    VhvActions.TRAGERISIKO_CHANGED,
    VhvActions.MAP_COORDINATES_VHV_CHANGED_ANGEBOT,
  ];
  const successActions = [
    StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_MIT_INFO_VHV_V2,
    StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_OHNE_INFO_VHV_V2,
  ];

  streckenTarifierer = new StreckenTarifierer(
    {
      appId: App.VHV_V2,
      successEventWithInfo:
        StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_MIT_INFO_VHV_V2,
      successEventWithoutInfo:
        StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_OHNE_INFO_VHV_V2,
      errorEvent: StreckenTarifierungRequestEvents.PREISE_REQUESTED_FAILED_VHV_V2,
    },
    buildTaasVHVDataForStrecke,
    undefined,
    createHashedRelevantRiskdataVHV,
  );

  yield takeLatest(requestActions, safeSaga(callTarifierungUnlessEntschaedigungsgrenzeInvalid, App.VHV_V2));
  yield takeEvery(successActions, safeSaga(streckenTarifierer.handleSuccess, App.VHV_V2));
  yield takeEvery(
    StreckenTarifierungRequestEvents.PREISE_REQUESTED_FAILED_VHV_V2,
    safeSaga(streckenTarifierer.handleError, App.VHV_V2),
  );
}

export function* callTarifierungUnlessEntschaedigungsgrenzeInvalid(
  action: AnyAction,
): Generator<SelectEffect | CallEffect> {
  const state: IAppState = (yield select()) as IAppState;
  if (state.wertsachensummen && !hasInvalidEntschaedigungsgrenzeRad(state)) {
    yield call(streckenTarifierer.callTarifierung, action);
  }
}

export function hasInvalidEntschaedigungsgrenzeRad(state: IAppState): boolean {
  const selectedProduktlinie = getSelectedProduktlinie(state.produkt);
  if (!selectedProduktlinie) {
    return false;
  }
  const linie = (state.entschaedigungsgrenzeRad ?? []).find(
    grenze => grenze.produktlinie === selectedProduktlinie.produktName,
  );
  if (!linie || !linie.available || isEntschaedigungsgrenzeResetFromBff(linie)) {
    return false;
  }
  return !isEntschaedigungsgrenzeValid(linie.betrag, linie.minInput, linie.maxInput);
}

export function createHashedRelevantRiskdataVHV(taasData: TaasVhvV2Data): string {
  const relevantData = { ...taasData };

  delete relevantData.gisDaten;
  delete relevantData.plzFaktorenAusgabe;
  delete relevantData.salesProducts;
  delete relevantData.spartenType;
  delete relevantData.versicherungssumme;
  delete relevantData.versicherungssummenRad;
  delete relevantData.entschaedigungsgrenzeWertsachenBetrag;

  return createHashedTaasData(relevantData);
}
